import { type EmbedTargetType } from './embed-target'
import { HtmlMediaElementControls } from '../media/HtmlMediaControls'
import { type MediaControls } from '../media'
import { type DetectEmbedTargetsOptions, type EmbedTargetFactory } from './embed-target-factory'
import { BaseEmbedTarget } from './base'
import { type EmbedBeforeCreateElementContext, type EmbedElementContext } from '../element-embedder'
import { type IFrameEmbedHandler } from '../embed-strategies/iframe/IFrameEmbedStrategy'
import { DefaultParentStrategyHandler } from '../embed-strategies/iframe/ParentStrategyHandler'
import { FloatStrategyHandler } from '../embed-strategies/iframe/FloatStrategyHandler'

export class HTMLMediaEmbedTargetFactory implements EmbedTargetFactory<HTMLMediaEmbedTarget> {
  name: EmbedTargetType = 'html5'
  supports (ctx: EmbedBeforeCreateElementContext): boolean {
    if (ctx.targetEl.tagName !== 'VIDEO') return false
    // TODO enhanced parent check
    return (ctx.settings.strategy === 'parent' && ctx.targetEl.parentElement?.tagName === 'DIV') || ctx.settings.strategy !== 'parent'
  }

  create (ctx: EmbedElementContext): HTMLMediaEmbedTarget {
    return new HTMLMediaEmbedTarget(ctx)
  }

  detectFromDom ({ strategy }: DetectEmbedTargetsOptions): HTMLElement[] {
    const targetEls: HTMLElement[] = []
    document.querySelectorAll('video').forEach(el => {
      if (strategy === 'parent') (el.parentElement != null) && targetEls.push(el)
      else targetEls.push(el)
    })
    return targetEls
  }
}

export class HTMLMediaEmbedTarget extends BaseEmbedTarget {
  mediaControl?: MediaControls

  protected getIFrameEmbedHandler (): IFrameEmbedHandler {
    const strategy = this.ctx.settings.strategy
    if (strategy === 'parent') return new DefaultParentStrategyHandler(this)
    else if (strategy === 'float-absolute' || strategy === 'float-fixed') return new FloatStrategyHandler(this)
    throw new Error(`Invalid strategy '${strategy as string}' for HTMLMediaEmbedTarget`)
  }

  constructor (ctx: EmbedElementContext) {
    super('html5', ctx)
    const mediaElement = this.ctx.targetEl
    if (mediaElement instanceof HTMLMediaElement) this.mediaControl = new HtmlMediaElementControls(mediaElement)
  }

  get bottomSpace (): string {
    return this.ctx.settings.bottomSpace ?? '80px'
  }

  setPlaysInline (value: boolean) {
    if (this.ctx.targetEl instanceof HTMLVideoElement) this.ctx.targetEl.playsInline = value
  }
}
